import React from 'react'
import { useTranslation } from 'react-i18next'
import { BellOff, BellRing, Download, Smartphone, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { useRoute } from '@/lib/RouteContext'
import { enablePush, promptInstall, refreshPush, usePushState, type EnableResult, type PushPhase } from '@/lib/push/pushStore'
import { clearFreshLogin, isFreshLogin, isSnoozed, snooze, startSession, touchSession } from '@/lib/push/session'
import { OPEN_PUSH_PROMPT_EVENT, type PushDialogStep } from '@/lib/push/events'
import { cn } from '@/lib/utils'
import { BlockedSteps, IosInstallSteps, PrivacyLink } from './PushSteps'

type DialogStep = PushDialogStep

const DIALOG_PHASES: DialogStep[] = ['install', 'openInstalled', 'enable', 'blocked']
const TOUCH_EVERY_MS = 60_000

/**
 * F-186 (David: insistent, "for the best experience", no "don't ask again"):
 * - right after login, a modal: install (iPhone steps, or the browser's
 *   Instalar), then turning notifications on; on the iPhone the installed app
 *   asks for the new login and then for notifications;
 * - afterwards a thin bar under the header (one line, pushes the content) for
 *   whatever is still unresolved: install, enable, or how to unblock. "Agora
 *   não" hides it until the next session only.
 * Also reconciles this browser's subscription when the session opens and
 * routes a notification click (from the service worker) inside the app.
 */
export function PushPrompts() {
  const { t } = useTranslation('push')
  const { user, mustChangePassword } = useAuth()
  const { community } = useCommunity()
  const { navigate } = useRoute()
  const push = usePushState()
  const [dialog, setDialog] = React.useState<DialogStep | null>(null)
  const [snoozed, setSnoozed] = React.useState(() => { startSession(); return isSnoozed() })
  const [message, setMessage] = React.useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)

  /* The session's member id (stable), not the resolved member, which goes null while the role reloads
     and would reset the decision mid-session. */
  const sessionKey = user?.id && community?.id ? `${community.id}:${user.id}` : ''
  /* Right after login the modal speaks first: no bar until it has been decided (one render, no flash). */
  const [, setFreshHandled] = React.useState(0)
  const freshPending = Boolean(sessionKey) && isFreshLogin()

  React.useEffect(() => {
    void refreshPush(sessionKey)
  }, [sessionKey])

  /* A new login is a new session: the bar comes back. */
  React.useEffect(() => {
    if (sessionKey && isFreshLogin()) setSnoozed(false)
  }, [sessionKey])

  /* The session stays alive while the app is in use. */
  React.useEffect(() => {
    const touch = () => { if (document.visibilityState === 'visible') touchSession() }
    const timer = window.setInterval(touch, TOUCH_EVERY_MS)
    document.addEventListener('visibilitychange', touch)
    window.addEventListener('pagehide', touch)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', touch)
      window.removeEventListener('pagehide', touch)
    }
  }, [])

  /* A notification click: the service worker focuses this tab and sends the path. */
  React.useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; path?: string } | null
      if (data?.type === 'memberfy:navigate' && typeof data.path === 'string' && data.path.startsWith('/') && !data.path.startsWith('//')) navigate(data.path)
    }
    navigator.serviceWorker.addEventListener('message', onMessage)
    return () => navigator.serviceWorker.removeEventListener('message', onMessage)
  }, [navigate])

  /* Right after login, once the phase is known: the modal, once. */
  React.useEffect(() => {
    if (!sessionKey || push.phase === 'off' || !isFreshLogin()) return
    clearFreshLogin()
    if (push.phase === 'install' || push.phase === 'openInstalled' || push.phase === 'enable') setDialog(push.phase)
    setFreshHandled((n) => n + 1)
  }, [sessionKey, push.phase])

  /* The avatar panel asks for a step. */
  React.useEffect(() => {
    const onOpen = (event: Event) => {
      const step = (event as CustomEvent<{ step?: DialogStep }>).detail?.step
      if (step) setDialog(step)
    }
    window.addEventListener(OPEN_PUSH_PROMPT_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_PUSH_PROMPT_EVENT, onOpen)
  }, [])

  /* The dialog follows the phase (installed → enable; enabled → closes). */
  React.useEffect(() => {
    if (!dialog) return
    if (push.phase === 'done' || push.phase === 'off' || push.phase === 'unsupported') setDialog(null)
    else if (dialog === 'install' && push.phase === 'enable') setDialog('enable')
    else if (dialog === 'enable' && push.phase === 'blocked') setDialog('blocked')
  }, [dialog, push.phase])

  React.useEffect(() => {
    if (!message) return
    const timer = window.setTimeout(() => setMessage(null), 6000)
    return () => window.clearTimeout(timer)
  }, [message])

  const communityName = community?.name || 'Memberfy'

  const onEnableResult = (result: EnableResult) => {
    if (result === 'enabled') setMessage({ tone: 'ok', text: t('enable.done') })
    else if (result === 'dismissed') setMessage({ tone: 'warn', text: t('enable.dismissed') })
    else if (result === 'failed') setMessage({ tone: 'warn', text: t('enable.failed') })
  }
  /* Straight from the click: `enablePush` asks the browser before anything else. */
  const enable = () => { void enablePush().then(onEnableResult) }
  const install = () => {
    if (push.canPromptInstall) void promptInstall()
    else setDialog('install')
  }
  const later = () => { snooze(); setSnoozed(true) }

  const barPhase = (DIALOG_PHASES as PushPhase[]).includes(push.phase) ? (push.phase as DialogStep) : null
  const showBar = Boolean(sessionKey && barPhase && !freshPending && !snoozed && !dialog && !mustChangePassword)

  return (
    <>
      {showBar && barPhase && (
        <PushBar phase={barPhase} canPromptInstall={push.canPromptInstall} busy={push.busy} onInstall={install} onEnable={enable} onHow={() => setDialog(barPhase)} onLater={later} />
      )}
      {message && (
        <div role="status" className={cn('border-b px-4 py-2 text-sm sm:px-6', message.tone === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900')} data-push-message>
          {message.text}
        </div>
      )}
      <Dialog open={Boolean(dialog)} onOpenChange={(open) => { if (!open) setDialog(null) }}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto p-4 sm:p-6" data-push-dialog={dialog ?? undefined} aria-describedby={undefined}>
          <div className="min-w-0">
            {dialog === 'install' && (
              <InstallStep name={communityName} ios={push.platform === 'ios'} canPromptInstall={push.canPromptInstall} onInstall={() => void promptInstall()} onLater={() => setDialog(null)} />
            )}
            {dialog === 'openInstalled' && (
              <SimpleStep icon={<Smartphone className="h-5 w-5" />} title={t('openInstalled.title')} body={t('openInstalled.body', { name: communityName })} onLater={() => setDialog(null)} laterLabel={t('ok')} />
            )}
            {dialog === 'enable' && (
              <SimpleStep icon={<BellRing className="h-5 w-5" />} title={t('enable.title')} body={t('enable.body')} onLater={() => setDialog(null)}
                action={<Button type="button" variant="secondary" className="w-full sm:w-auto" disabled={push.busy} onClick={enable} data-push-enable>{t('enable.button')}</Button>} />
            )}
            {dialog === 'blocked' && (
              <div className="space-y-4">
                <DialogHeader className="text-left">
                  <DialogTitle className="flex items-center gap-2 pr-8"><BellOff className="h-5 w-5 flex-shrink-0 text-amber-600" />{t('blocked.title')}</DialogTitle>
                  <DialogDescription>{t('blocked.body')}</DialogDescription>
                </DialogHeader>
                <BlockedSteps kind={push.kind} />
                <PrivacyLink />
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => setDialog(null)}>{t('ok')}</Button>
                </DialogFooter>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const SimpleStep = ({ icon, title, body, action, onLater, laterLabel }: { icon: React.ReactNode; title: string; body: string; action?: React.ReactNode; onLater: () => void; laterLabel?: string }) => {
  const { t } = useTranslation('push')
  return (
    <div className="space-y-4">
      <DialogHeader className="text-left">
        <DialogTitle className="flex items-center gap-2 pr-8"><span className="flex-shrink-0 text-primary">{icon}</span>{title}</DialogTitle>
        <DialogDescription>{body}</DialogDescription>
      </DialogHeader>
      <PrivacyLink />
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onLater} data-push-later>{laterLabel ?? t('later')}</Button>
        {action}
      </DialogFooter>
    </div>
  )
}

const InstallStep = ({ name, ios, canPromptInstall, onInstall, onLater }: { name: string; ios: boolean; canPromptInstall: boolean; onInstall: () => void; onLater: () => void }) => {
  const { t } = useTranslation('push')
  return (
    <div className="space-y-4">
      <DialogHeader className="text-left">
        <DialogTitle className="flex items-center gap-2 pr-8"><Download className="h-5 w-5 flex-shrink-0 text-primary" />{t('install.title', { name })}</DialogTitle>
        <DialogDescription>{ios ? t('install.iosIntro') : t('install.body')}</DialogDescription>
      </DialogHeader>
      {ios && (
        <>
          <IosInstallSteps />
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-push-relogin>{t('install.iosRelogin')}</p>
        </>
      )}
      <PrivacyLink />
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={onLater} data-push-later>{t('later')}</Button>
        {!ios && canPromptInstall && (
          <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={onInstall} data-push-install>{t('install.button')}</Button>
        )}
      </DialogFooter>
    </div>
  )
}

interface BarProps {
  phase: DialogStep
  canPromptInstall: boolean
  busy: boolean
  onInstall: () => void
  onEnable: () => void
  onHow: () => void
  onLater: () => void
}

/** One thin line under the header (≤ 44 px on a phone, 40 px on a computer); it pushes the page down, never covers it. */
const PushBar = ({ phase, canPromptInstall, busy, onInstall, onEnable, onHow, onLater }: BarProps) => {
  const { t } = useTranslation('push')
  const blocked = phase === 'blocked'
  const text = t(`bar.${phase}`)
  const short = t(`bar.${phase}Short`)
  const action = phase === 'install'
    ? { label: canPromptInstall ? t('install.button') : t('bar.howTo'), run: onInstall }
    : phase === 'enable'
      ? { label: t('enable.button'), run: onEnable }
      : { label: phase === 'blocked' ? t('bar.blockedHow') : t('bar.openInstalledHow'), run: onHow }
  const Icon = blocked ? BellOff : phase === 'enable' ? BellRing : Smartphone
  /* David (2026-10-29): insistent, so the community's solid color (shade 600, the solid buttons' background)
     with its readable text color (`--color-primary-foreground`, white or near black by contrast); the action
     inverts the two. Rectangular. "Blocked" is an error state and keeps the amber warning. */
  return (
    <div
      role="region"
      aria-label={text}
      data-push-bar={phase}
      className={cn(
        'flex h-11 items-center gap-2 border-b px-4 text-sm sm:h-10 sm:gap-3 sm:px-6',
        blocked ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-transparent bg-primary-600 font-medium text-primary-foreground'
      )}
    >
      <Icon className={cn('h-4 w-4 flex-shrink-0', blocked && 'text-amber-600')} aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        <span className="sm:hidden">{short}</span>
        <span className="hidden sm:inline">{text}</span>
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={action.run}
        data-push-bar-action
        className={cn(
          'inline-flex h-7 flex-shrink-0 items-center px-3 text-xs font-semibold transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-60',
          blocked ? 'border border-amber-300 bg-white text-amber-900 focus-visible:ring-amber-500' : 'bg-primary-foreground text-primary-600 focus-visible:ring-primary-foreground focus-visible:ring-offset-primary-600'
        )}
      >
        {action.label}
      </button>
      <button
        type="button"
        onClick={onLater}
        data-push-bar-later
        className="hidden flex-shrink-0 text-xs underline-offset-2 opacity-80 hover:underline hover:opacity-100 focus-visible:underline focus-visible:outline-none sm:inline"
      >
        {t('later')}
      </button>
      <button type="button" className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center opacity-80 hover:opacity-100 sm:hidden" onClick={onLater} aria-label={t('later')} data-push-bar-later-icon>
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
